import { defineStore } from 'pinia'
import { ref, computed, readonly } from 'vue'
import { cartAPI } from '../api/modules/cart.js'
import { event } from 'vue-gtag'
import * as Sentry from '@sentry/vue'

export const useCartStore = defineStore('cart', () => {
  // State
  const cart = ref(null)
  const items = ref([])
  const isLoading = ref(false)
  const error = ref(null)
  const isTransferPending = ref(false)
  const reservations = ref([])
  const reservationTimers = ref({})

  // Computed
  const itemCount = computed(() => cart.value?.items_count || 0)
  const totalQuantity = computed(() => cart.value?.total_quantity || 0)
  // Compute totals from items, overriding for B2B custom pricing
  const computedSubtotal = computed(() => {
    if (!cart.value?.items) return 0
    return cart.value.items.reduce((sum, item) => {
      // Use B2B custom price when available; disable bulk discount
      if (item.product?.has_custom_pricing) {
        const cp = parseFloat(item.product?.custom_pricing?.custom_price ?? item.final_price ?? item.unit_price ?? 0)
        return sum + cp * (item.quantity || 0)
      }
      // Fallback to backend line total for non-B2B items
      const line = parseFloat(item.total_price ?? 0)
      if (!isNaN(line)) return sum + line
      const unit = parseFloat(item.final_price ?? item.unit_price ?? 0)
      return sum + unit * (item.quantity || 0)
    }, 0)
  })
  const discountAmount = computed(() => parseFloat(cart.value?.discount_amount || 0))
  const subtotal = computed(() => computedSubtotal.value)
  const total = computed(() => Math.max(0, computedSubtotal.value - discountAmount.value))
  const hasItems = computed(() => itemCount.value > 0)
  const hasDiscount = computed(() => cart.value?.has_discount || false)
  const appliedDiscountCode = computed(() => cart.value?.discount_code || null)
  const totalSavings = computed(() => {
    let savings = 0
    if (cart.value?.items) {
      cart.value.items.forEach(item => {
        // Ignore bulk discount savings for B2B items
        if (!item.product?.has_custom_pricing && item.bulk_discount_applied) {
          savings += item.bulk_discount_applied.total_savings || 0
        }
      })
    }
    return savings + discountAmount.value
  })

  // Calculate shipping cost (check for free shipping from discount code)
  const calculateShippingCost = (shippingMethod) => {
    if (!shippingMethod) return 0
    
    // Check if applied discount code includes free shipping
    if (cart.value?.discount_code_details?.free_shipping) {
      return 0
    }
    
    // Handle weight_tiers pricing
    if (shippingMethod.price_type === 'weight_tiers' && shippingMethod.weight_tiers?.length > 0) {
      // Calculate total cart weight in kg
      const totalWeight = cart.value?.items?.reduce((sum, item) => {
        // Own spice mix: the grams its package holds
        if (item.custom_mix) return sum + ((item.custom_mix.package?.fill_grams || 0) / 1000) * item.quantity

        const weight = parseFloat(item.product?.weight || 0)
        const unit = item.product?.weight_unit || 'kg'
        
        // Convert to kg based on unit
        let weightInKg = weight
        switch (unit) {
          case 'g':
            weightInKg = weight / 1000
            break
          case 'lb':
            weightInKg = weight * 0.453592
            break
          case 'oz':
            weightInKg = weight * 0.0283495
            break
          case 'kg':
          default:
            weightInKg = weight
        }
        
        return sum + (weightInKg * item.quantity)
      }, 0) || 0
      
      // Find the appropriate tier based on weight
      // Sort tiers by max_weight ascending to find the first tier that fits
      const sortedTiers = [...shippingMethod.weight_tiers].sort((a, b) => 
        parseFloat(a.max_weight) - parseFloat(b.max_weight)
      )
      
      console.log(`[ShippingCost] Method: ${shippingMethod.name}, Weight: ${totalWeight}kg, Tiers:`, sortedTiers.map(t => `${t.max_weight}kg→€${t.price}`))
      
      const tier = sortedTiers.find(t => parseFloat(t.max_weight) >= totalWeight)
      
      if (tier) {
        console.log(`[ShippingCost] Selected tier: ${tier.max_weight}kg→€${tier.price}`)
        return parseFloat(tier.price || 0)
      }
      
      // If no tier found (weight exceeds all tiers), return 0 or the last tier price
      const lastTierPrice = parseFloat(sortedTiers[sortedTiers.length - 1]?.price || 0)
      console.log(`[ShippingCost] No matching tier, using last: €${lastTierPrice}`)
      return lastTierPrice
    }
    
    return parseFloat(shippingMethod.price || 0)
  }

  // Computed - Reservation helpers
  const hasExpiredReservations = computed(() => {
    return reservations.value.some(r => r.time_remaining_seconds <= 0)
  })
  
  const getReservationForProduct = (productId) => {
    return reservations.value.find(r => r.product_id === productId)
  }

  // Actions
  const setCart = (cartData) => {
    cart.value = cartData
    items.value = cartData?.items || []
    
    // Process reservations and calculate initial time remaining from expires_at
    const rawReservations = cartData?.reservations_with_time || []
    reservations.value = rawReservations.map(reservation => {
      const expiresAt = new Date(reservation.expires_at)
      const now = new Date()
      const secondsRemaining = Math.max(0, Math.floor((expiresAt - now) / 1000))
      
      return {
        ...reservation,
        time_remaining_seconds: secondsRemaining,
        time_remaining_minutes: Math.ceil(secondsRemaining / 60)
      }
    })
    
    startReservationTimers()
  }

  const clearCart = () => {
    cart.value = null
    items.value = []
    reservations.value = []
    error.value = null
    stopReservationTimers()
  }
  
  // Reservation timer management
  const startReservationTimers = () => {
    stopReservationTimers() // Clear existing timers
    
    reservations.value.forEach(reservation => {
      if (reservation.time_remaining_seconds > 0) {
        const timer = setInterval(() => {
          updateReservationTime(reservation.product_id)
        }, 1000) // Update every second
        
        reservationTimers.value[reservation.product_id] = timer
      }
    })
  }
  
  const stopReservationTimers = () => {
    Object.values(reservationTimers.value).forEach(timer => clearInterval(timer))
    reservationTimers.value = {}
  }
  
  const updateReservationTime = (productId) => {
    const reservation = reservations.value.find(r => r.product_id === productId)
    if (reservation) {
      // Calculate time remaining from expires_at timestamp
      const expiresAt = new Date(reservation.expires_at)
      const now = new Date()
      const secondsRemaining = Math.floor((expiresAt - now) / 1000)
      
      reservation.time_remaining_seconds = Math.max(0, secondsRemaining)
      reservation.time_remaining_minutes = Math.ceil(reservation.time_remaining_seconds / 60)
      
      // Stop timer when expired
      if (reservation.time_remaining_seconds <= 0) {
        if (reservationTimers.value[productId]) {
          clearInterval(reservationTimers.value[productId])
          delete reservationTimers.value[productId]
        }
      }
    }
  }
  
  const formatReservationTime = (seconds) => {
    // Handle negative or zero values
    const actualSeconds = Math.max(0, seconds)
    if (actualSeconds === 0) return '0:00'
    
    const mins = Math.floor(actualSeconds / 60)
    const secs = actualSeconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const initCart = async () => {
    try {
      await getCart()
    } catch (error) {
      console.error('Failed to initialize cart:', error)
    }
  }

  const getCart = async () => {
    try {
      isLoading.value = true
      error.value = null
      
      Sentry.logger.debug('Fetching cart')
      
      // Skip cart initialization if transfer is pending
      if (isTransferPending.value) {
        return { success: true, data: cart.value }
      }
      
      const response = await cartAPI.getCart()
      
      // Some backends may return 204 (no content). If so, don't clear existing cart.
      if (response) {
        setCart(response)
        Sentry.logger.debug('Cart fetched successfully', {
          item_count: response.items?.length || 0,
          cart_id: response.id
        })
        return { success: true, data: response }
      }
      // No content: leave cart as-is and report success without new data
      return { success: true, data: cart.value }
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to load cart'
      Sentry.logger.error('Failed to fetch cart', {
        error: error.value,
        status_code: err.response?.status
      })
      return { success: false, error: error.value }
    } finally {
      isLoading.value = false
    }
  }

  const addItem = async (productId, quantity = 1) => {
    try {
      isLoading.value = true
      error.value = null
      
      Sentry.logger.info('Adding item to cart', {
        product_id: productId,
        quantity: quantity
      })
      
      const response = await cartAPI.addItem({ product_id: productId, quantity })
      
      if (response && response.id) {
        setCart(response)
        
        // Track add to cart event
        const addedItem = response.items?.find(item => item.product_id === productId)
        if (addedItem) {
          event('add_to_cart', {
            currency: 'EUR',
            value: parseFloat(addedItem.final_price || addedItem.unit_price || 0) * quantity,
            items: [{
              item_id: addedItem.product_id,
              item_name: addedItem.product?.name || 'Unknown',
              price: parseFloat(addedItem.final_price || addedItem.unit_price || 0),
              quantity: quantity
            }]
          })
          
          Sentry.logger.info('Item added to cart successfully', {
            product_id: productId,
            product_name: addedItem.product?.name,
            quantity: quantity,
            price: parseFloat(addedItem.final_price || addedItem.unit_price || 0),
            cart_id: response.id
          })
        }
        
        return { success: true, data: response }
      }
      // Fallback: fetch cart if mutation returned no content (e.g., 204)
      const refreshed = await cartAPI.getCart()
      if (refreshed) {
        setCart(refreshed)
        return { success: true, data: refreshed }
      }
      return { success: false, error: 'Cart not returned after add' }
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to add item to cart'
      Sentry.logger.error('Failed to add item to cart', {
        product_id: productId,
        quantity: quantity,
        error: error.value,
        status_code: err.response?.status
      })
      return { success: false, error: error.value }
    } finally {
      isLoading.value = false
    }
  }

  const updateItem = async (cartItemId, quantity) => {
    try {
      // Don't set isLoading to prevent hiding cart during update
      error.value = null
      
      Sentry.logger.info('Updating cart item', {
        cart_item_id: cartItemId,
        new_quantity: quantity
      })
      
      const response = await cartAPI.updateItem(cartItemId, { quantity })
      
      if (response && response.items) {
        setCart(response)
        Sentry.logger.info('Cart item updated successfully', {
          cart_item_id: cartItemId,
          new_quantity: quantity
        })
        return { success: true, data: response }
      }
      const refreshed = await cartAPI.getCart()
      if (refreshed) {
        setCart(refreshed)
        return { success: true, data: refreshed }
      }
      return { success: false, error: 'Cart not returned after update' }
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to update cart item'
      Sentry.logger.error('Failed to update cart item', {
        cart_item_id: cartItemId,
        new_quantity: quantity,
        error: error.value,
        status_code: err.response?.status
      })
      return { success: false, error: error.value }
    }
  }

  const removeItem = async (cartItemId) => {
    try {
      // Don't set isLoading to prevent hiding cart during removal
      error.value = null
      
      // Get item details before removing for tracking
      const removedItem = items.value.find(item => item.id === cartItemId)
      
      Sentry.logger.info('Removing item from cart', {
        cart_item_id: cartItemId,
        product_id: removedItem?.product_id,
        product_name: removedItem?.product?.name
      })
      
      const response = await cartAPI.removeItem(cartItemId)
      
      if (response && response.items) {
        setCart(response)
        
        // Track remove from cart event
        if (removedItem) {
          event('remove_from_cart', {
            currency: 'EUR',
            value: parseFloat(removedItem.total_price || 0),
            items: [{
              item_id: removedItem.product_id,
              item_name: removedItem.product?.name || 'Unknown',
              price: parseFloat(removedItem.final_price || removedItem.unit_price || 0),
              quantity: removedItem.quantity
            }]
          })
          
          Sentry.logger.info('Item removed from cart successfully', {
            cart_item_id: cartItemId,
            product_id: removedItem.product_id,
            product_name: removedItem.product?.name
          })
        }
        
        return { success: true, data: response }
      }
      const refreshed = await cartAPI.getCart()
      if (refreshed) {
        setCart(refreshed)
        return { success: true, data: refreshed }
      }
      return { success: false, error: 'Cart not returned after remove' }
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to remove cart item'
      Sentry.logger.error('Failed to remove cart item', {
        cart_item_id: cartItemId,
        error: error.value,
        status_code: err.response?.status
      })
      return { success: false, error: error.value }
    }
  }

  const applyDiscount = async (discountCode) => {
    try {
      isLoading.value = true
      error.value = null
      
      Sentry.logger.info('Applying discount code', {
        discount_code: discountCode
      })
      
      const response = await cartAPI.applyDiscount(discountCode)
      
      if (response && response.items) {
        setCart(response)
        Sentry.logger.info('Discount code applied successfully', {
          discount_code: discountCode,
          discount_amount: response.discount_amount,
          cart_id: response.id
        })
        return { success: true, data: response }
      }
      const refreshed = await cartAPI.getCart()
      if (refreshed) {
        setCart(refreshed)
        return { success: true, data: refreshed }
      }
      return { success: false, error: 'Cart not returned after applying discount' }
    } catch (err) {
      error.value = err.response?.data?.message || 'Zadaný kód nie je platný'
      Sentry.logger.warn('Failed to apply discount code', {
        discount_code: discountCode,
        error: error.value,
        status_code: err.response?.status
      })
      return { success: false, error: error.value }
    } finally {
      isLoading.value = false
    }
  }

  const removeDiscount = async () => {
    try {
      isLoading.value = true
      error.value = null
      
      const previousCode = cart.value?.discount_code
      
      Sentry.logger.info('Removing discount code', {
        discount_code: previousCode
      })
      
      const response = await cartAPI.removeDiscount()
      
      if (response && response.items) {
        setCart(response)
        Sentry.logger.info('Discount code removed successfully', {
          previous_discount_code: previousCode
        })
        return { success: true, data: response }
      }
      const refreshed = await cartAPI.getCart()
      if (refreshed) {
        setCart(refreshed)
        return { success: true, data: refreshed }
      }
      return { success: false, error: 'Cart not returned after removing discount' }
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to remove discount code'
      Sentry.logger.error('Failed to remove discount code', {
        error: error.value,
        status_code: err.response?.status
      })
      return { success: false, error: error.value }
    } finally {
      isLoading.value = false
    }
  }

  const clearCartData = async () => {
    try {
      isLoading.value = true
      error.value = null
      
      Sentry.logger.info('Clearing cart', {
        cart_id: cart.value?.id,
        item_count: items.value?.length || 0
      })
      
      const response = await cartAPI.clearCart()
      
      clearCart()
      
      Sentry.logger.info('Cart cleared successfully')
      
      return { success: true, data: response }
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to clear cart'
      Sentry.logger.error('Failed to clear cart', {
        error: error.value,
        status_code: err.response?.status
      })
      return { success: false, error: error.value }
    } finally {
      isLoading.value = false
    }
  }

  const transferCart = async (guestCartId) => {
    try {
      isLoading.value = true
      error.value = null
      isTransferPending.value = true
      
      Sentry.logger.info('Transferring guest cart', {
        guest_cart_id: guestCartId
      })
      
      const response = await cartAPI.transferCart(guestCartId)
      
      if (response && response.items) {
        setCart(response)
        Sentry.logger.info('Cart transferred successfully', {
          guest_cart_id: guestCartId,
          new_cart_id: response.id,
          item_count: response.items?.length || 0
        })
        return { success: true, data: response }
      }
      const refreshed = await cartAPI.getCart()
      if (refreshed) {
        setCart(refreshed)
        return { success: true, data: refreshed }
      }
      return { success: false, error: 'Cart not returned after transfer' }
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to transfer cart'
      Sentry.logger.error('Failed to transfer cart', {
        guest_cart_id: guestCartId,
        error: error.value,
        status_code: err.response?.status
      })
      return { success: false, error: error.value }
    } finally {
      isLoading.value = false
      isTransferPending.value = false
    }
  }

  // Helper functions
  const findItemByProductId = (productId) => {
    return items.value.find(item => item.product_id === productId)
  }

  const getItemQuantity = (productId) => {
    const item = findItemByProductId(productId)
    return item?.quantity || 0
  }

  const isProductInCart = (productId) => {
    return !!findItemByProductId(productId)
  }

  const getCartSummary = () => {
    return {
      itemCount: itemCount.value,
      totalQuantity: totalQuantity.value,
      subtotal: subtotal.value,
      discountAmount: discountAmount.value,
      total: total.value,
      hasDiscount: hasDiscount.value,
      appliedDiscountCode: appliedDiscountCode.value,
      totalSavings: totalSavings.value
    }
  }

  return {
    // State
    cart: readonly(cart),
    items: readonly(items),
    isLoading: readonly(isLoading),
    error: readonly(error),
    
    // Computed
    itemCount,
    totalQuantity,
    subtotal,
    discountAmount,
    total,
    hasItems,
    hasDiscount,
    appliedDiscountCode,
    totalSavings,
    hasExpiredReservations,
    
    // Reservations
    reservations: readonly(reservations),
    reservationTimers: readonly(reservationTimers),
    getReservationForProduct,
    formatReservationTime,
    
    // Actions
    setCart,
    clearCart,
    initCart,
    getCart,
    addItem,
    updateItem,
    removeItem,
    applyDiscount,
    removeDiscount,
    clearCartData,
    transferCart,
    
    // Helpers
    findItemByProductId,
    getItemQuantity,
    isProductInCart,
    getCartSummary,
    calculateShippingCost
  }
})
